type DataLayerPayload = Record<string, unknown>;

type SelectionSource = "map" | "sidebar" | "shortcut";
type RouteType = "normal" | "barrier_free";

/**
 * dataLayer へイベントを push する共通関数
 */
const pushDataLayerEvent = (eventName: string, payload?: DataLayerPayload) => {
  if (typeof window === "undefined") {
    return;
  }
  // キオスクビルドは GTM を読み込まない (vite.config.ts) ので、送られない dataLayer を溜め続けない
  if (import.meta.env.VITE_KIOSK_MODE === "true") {
    return;
  }

  const dataLayer = (window as Window).dataLayer || [];
  (window as Window).dataLayer = dataLayer;

  dataLayer.push({
    event: eventName,
    ...(payload ?? {}),
  });
};

export const trackFacilitySelection = (params: {
  facilityId: string;
  facilityName?: string;
  selectionSource: SelectionSource;
}) => {
  pushDataLayerEvent("select_facility", {
    facility_id: params.facilityId,
    facility_name: params.facilityName ?? params.facilityId,
    selection_source: params.selectionSource,
  });
};

export const trackRouteSearchStart = (params: {
  facilityId: string;
  facilityName?: string;
  routeType: RouteType;
}) => {
  pushDataLayerEvent("start_route_search", {
    facility_id: params.facilityId,
    facility_name: params.facilityName ?? params.facilityId,
    route_type: params.routeType,
  });
};

export const trackUIButtonClick = (buttonName: string) => {
  pushDataLayerEvent("click_ui_button", {
    button_name: buttonName,
  });
};

export const trackSearchQuery = (searchTerm: string) => {
  if (!searchTerm) return;

  pushDataLayerEvent("search", {
    search_term: searchTerm,
  });
};

export const trackCategorySelection = (category: string) => {
  pushDataLayerEvent("select_category", {
    category,
  });
};

/**
 * サイネージ1起動につき1回送るセッション開始イベント。
 */
export const trackSessionStart = (params: { sessionId: string; startedAt: string }) => {
  pushDataLayerEvent("session_start", {
    kiosk_session_id: params.sessionId,
    session_started_at: params.startedAt,
  });
};

/**
 * 一定間隔で送る心拍。滞在時間の概算に利用する。
 */
export const trackSessionHeartbeat = (params: { sessionId: string; elapsedSeconds: number }) => {
  pushDataLayerEvent("session_heartbeat", {
    kiosk_session_id: params.sessionId,
    elapsed_seconds: params.elapsedSeconds,
  });
};

/**
 * 終了時に送るイベント。duration は秒数。
 */
export const trackSessionEnd = (params: { sessionId: string; durationSeconds: number }) => {
  pushDataLayerEvent("session_end", {
    kiosk_session_id: params.sessionId,
    session_duration: params.durationSeconds,
  });
};

export const trackRouteFeedback = (isHelpful: boolean) => {
  pushDataLayerEvent("route_feedback", {
    helpful: isHelpful ? "yes" : "no",
  });
};
